import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useProjectStore } from '@/store/projectStore'
import { useJourneyStore } from '@/store/journeyStore'
import { useOutOfBandChanges } from '@/hooks/useOutOfBandChanges'
import { useDockUiStore } from '@/store/dockUiStore'
import { createBlankProject } from '@/api/workbench'
import { getJsonRaw, saveJson } from '@/api/generateapp'
import toast from 'react-hot-toast'
import type { RawAppData } from '@/types'
import Modal from '@/components/common/Modal'
import AppIcon from '@/components/common/AppIcon'
import NoAppDialog, { isNoAppDialogDismissed } from '@/components/common/NoAppDialog'
import StartCard from '@/components/common/StartCard'
import { useAuth } from '@/auth/AuthContext'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import {
  LayoutGrid, FolderOpen, Sparkles, BarChart3, FileCode, GitBranch, Library, Coins,
} from 'lucide-react'
import { useCreditsStore } from '@/store/creditsStore'
import { formatCredits } from '@/api/billing'
import SimpleWorkbench from './simple/SimpleWorkbench'
import { canonicalTab, redirectForTab } from './simple/tabs'
import { DeleteAppButton } from './settings/DangerZone'
import './simple/simple.css'

/* ----------------------------------------------------------------
   A project whose application.json is unreadable
   ----------------------------------------------------------------
   The old Overview tab's other duties moved into the simple Workbench:
   progress → Next step, Team → People, Variables / references / source
   control → Advanced, Application Properties / Save App / Delete → App
   settings. This view is what remains for a corrupted project. */

function InvalidProjectView() {
  const { currentProject } = useProjectStore()
  if (!currentProject) return null
  const folder = currentProject.application.folder_name
  return (
    <div className="swb swb-page">
      <div className="swb-card swb-card-pad" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', borderColor: 'var(--color-error)' }}>
        <span className="material-icons" aria-hidden="true" style={{ fontSize: 32, color: 'var(--color-error)' }}>error_outline</span>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '1.3125rem', color: 'var(--color-error)' }}>Invalid Project</h2>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            The project data for <strong>{folder}</strong> is corrupted or unreadable. This can happen when
            the application JSON is malformed or incomplete. You can delete it to clean up your workspace.
          </p>
        </div>
      </div>
      <div className="swb-card swb-card-pad"><DeleteAppButton folderName={folder} /></div>
    </div>
  )
}



/* ── Workbench landing page (no application open) ─────────────────
   This is what most users see first, so it does three jobs instead of
   repeating the same two buttons: it lets them jump straight back into an
   application they already have, it explains the ways to start a new one,
   and it says what the Workbench actually does once an app is open. */

/** What the Workbench gives you once an application is open. Purely
    informational — every one of these needs an app, so they aren't links. */
const WORKBENCH_CAPABILITIES: { icon: string; title: string; blurb: string }[] = [
  {
    icon: 'table_chart',
    title: 'Data model & transactions',
    blurb: 'Design tables and the forms, grids and lookups your users enter data through.',
  },
  {
    icon: 'web',
    title: 'Smart pages & sites',
    blurb: 'Build internal pages, customer portals and public websites from the same blocks.',
  },
  {
    icon: 'insights',
    title: 'Dashboards & visuals',
    blurb: 'Charts, KPIs and pivots over a shared data model, with drill-through and filters.',
  },
  {
    icon: 'approval',
    title: 'Approvals & processes',
    blurb: 'Multi-level sign-off before a record is saved, plus batch general processes.',
  },
  {
    icon: 'code',
    title: 'Business logic',
    blurb: 'Validations, field cascades and cross-updates in PL/pgSQL, Python or Java.',
  },
  {
    icon: 'rocket_launch',
    title: 'Deploy & run',
    blurb: 'Ship to a server, download a standalone runtime, or run it live in the browser.',
  },
]

function EmptyState() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { projects, selectProject, setActiveTab, fetchProjects } = useProjectStore()
  const setProjectModalOpen = useDockUiStore((s) => s.setProjectModalOpen)

  const sharedCount = projects.filter((p) => p.access && !p.access.is_owner).length
  // Show a handful of applications inline so the common case — "get me back
  // into the app I was working on" — is one click, not a modal round-trip.
  const shown = projects.slice(0, 6)
  // A brand-new account: no applications yet. Greeted as new (not "welcome
  // back") and told what it has to build with.
  const firstRun = projects.length === 0
  const credits = useCreditsStore()
  const { loaded: creditsLoaded, refresh: refreshCredits } = credits
  useEffect(() => {
    if (!creditsLoaded) void refreshCredits()
  }, [creditsLoaded, refreshCredits])
  const startingCredits =
    firstRun && credits.enabled && credits.balance != null && credits.balance > 0
      ? credits.balance : null

  // "Build Analytics Application" — create the project, then land straight on
  // the Analytics tab's Guided Build wizard (Connect a warehouse → pick tables
  // → describe → generate schema/areas/visuals/dashboards).
  const [analyticsOpen, setAnalyticsOpen] = useState(false)
  const [analyticsTitle, setAnalyticsTitle] = useState('')
  const [analyticsId, setAnalyticsId] = useState('')
  const [analyticsIdTouched, setAnalyticsIdTouched] = useState(false)
  const [creatingAnalytics, setCreatingAnalytics] = useState(false)

  const createAnalyticsApp = useCallback(async () => {
    const title = analyticsTitle.trim()
    const id = analyticsId.trim()
    if (!title) {
      toast.error('Give the analytics application a name')
      return
    }
    if (!/^[A-Za-z0-9_-]+$/.test(id)) {
      toast.error('Project ID must be letters, digits, _ or -')
      return
    }
    setCreatingAnalytics(true)
    try {
      const result = await createBlankProject(id, title)
      // An analytics application: its App type picks the analytics journey.
      try {
        const raw = (await getJsonRaw(result.project_id)) as RawAppData
        await saveJson(result.project_id, { ...raw, application: { ...raw.application, app_type: 'analytics' } })
      } catch (err) {
        console.error('[workbench] could not set the analytics app type', err)
      }
      await fetchProjects()
      // The Analytics workbench restores its section from sessionStorage, so
      // seeding it here opens the Guided Build wizard instead of the overview.
      sessionStorage.setItem(`twasta.analytics.section.${result.project_id}`, 'build')
      setActiveTab('data')
      await selectProject(result.project_id)
      setAnalyticsOpen(false)
      navigate(`/workbench/${encodeURIComponent(result.project_id)}/data`)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Could not create the analytics application')
    } finally {
      setCreatingAnalytics(false)
    }
  }, [analyticsTitle, analyticsId, fetchProjects, selectProject, setActiveTab, navigate])

  const openProject = useCallback((folderName: string) => {
    setActiveTab('next')
    selectProject(folderName)
    navigate(`/workbench/${encodeURIComponent(folderName)}/next`)
  }, [selectProject, setActiveTab, navigate])

  const sectionTitle = (text: string, hint?: string) => (
    <div style={{ marginBottom: '0.875rem' }}>
      <h2 style={{ fontSize: '0.875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-secondary)', margin: 0 }}>
        {text}
      </h2>
      {hint && (
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
          {hint}
        </p>
      )}
    </div>
  )

  return (
    <div style={{
      maxWidth: 980,
      margin: '0 auto',
      width: '100%',
      padding: '1.5rem 0 3rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '2.25rem',
      animation: 'fadeIn 0.25s ease-out',
    }}>
      {/* Hero — says what this place is, not just "select a project". */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          alignSelf: 'flex-start',
          padding: '4px 12px',
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: 'var(--color-text-secondary)',
        }}>
          <LayoutGrid size={12} />
          Workbench
        </span>
        <h1 style={{ fontSize: '1.8125rem', fontWeight: 700, margin: 0 }}>
          {firstRun
            ? (user?.user_name ? `Welcome to Twasta, ${user.user_name}` : 'Welcome to Twasta')
            : (user?.user_name ? `Welcome back, ${user.user_name}` : 'Welcome to your Workbench')}
        </h1>
        {startingCredits != null && (
          <p style={{
            margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start',
            padding: '0.375rem 0.75rem', borderRadius: 999, fontSize: '0.9375rem',
            background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
          }}>
            <Coins size={15} style={{ color: 'var(--color-accent)' }} />
            You have <strong>{formatCredits(startingCredits)} free credits</strong> to build your
            first app — no setup or API key needed.
          </p>
        )}
        <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '1rem', lineHeight: 1.7, maxWidth: 640 }}>
          This is where a whole business application gets built — data model, screens,
          business logic, dashboards and deployment, all driven by AI or by hand.
          Open an application below to pick up where you left off, or start a new one.
        </p>
        <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Sparkles size={14} style={{ color: 'var(--color-accent)' }} />
          New here? Ask anything about Twasta.ai in the panel on the left — what it can
          do, how to do something, or what you already have.
        </p>
      </div>

      {/* Your applications — the fastest path back into real work. */}
      {shown.length > 0 && (
        <div>
          {sectionTitle(
            'Your applications',
            sharedCount > 0
              ? `${projects.length} available, including ${sharedCount} shared with you.`
              : 'Pick one to open it in the Workbench.',
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {shown.map((p) => {
              const app = p.application
              const isDraft = app.group === 'draft' || !app.generated
              const isShared = !!p.access && !p.access.is_owner
              return (
                <button
                  key={app.folder_name}
                  type="button"
                  onClick={() => openProject(app.folder_name)}
                  title={`Open ${app.title} (${app.folder_name}) in the Workbench`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '0.75rem 0.875rem',
                    background: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-lg)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: 'var(--color-text)',
                    transition: 'all 160ms ease',
                    minWidth: 0,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
                >
                  <AppIcon
                    icon={app.icon}
                    imageData={app.icon_image_data}
                    size={34}
                    background={app.theme_color || 'var(--color-accent)'}
                    color="#fff"
                  />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: '0.9375rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {app.title}
                    </span>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {isShared ? 'Shared with you' : isDraft ? 'Draft — not generated yet' : `${p.navigation?.length ?? 0} module${(p.navigation?.length ?? 0) === 1 ? '' : 's'}`}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setProjectModalOpen(true)}
            style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <FolderOpen size={15} />
            {projects.length > shown.length
              ? `Browse all ${projects.length} applications`
              : 'Browse all applications'}
          </button>
        </div>
      )}

      {/* Ways to start — each explains what it actually does. */}
      <div>
        {sectionTitle(
          shown.length > 0 ? 'Start something new' : 'Start your first application',
          firstRun
            ? 'New to this? "Describe it in plain English" is the quickest start — or open a sample to look around first.'
            : 'Every way in ends up in the same Workbench.',
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
          <StartCard
            compact
            icon={Sparkles}
            title="Describe it in plain English"
            blurb="Tell the AI builder what the app should do and watch it create the data model, screens and logic."
            cta="Build with AI"
            onClick={() => navigate('/builder')}
          />
          <StartCard
            compact
            icon={Library}
            title="Start from a sample"
            blurb="Open a finished sample application to see how one is put together, then change it to suit you."
            cta="Browse samples"
            onClick={() => navigate('/samples')}
          />
          <StartCard
            compact
            icon={BarChart3}
            title="Build Analytics Application"
            blurb="Explore your data warehouse and generate analytics applications — connect Snowflake, BigQuery, Dremio or any database, pick your tables, and get schemas, visuals and dashboards."
            cta="Explore & generate"
            onClick={() => setAnalyticsOpen(true)}
          />
          <StartCard
            compact
            icon={FileCode}
            title="Replicate an existing app"
            blurb="Point Twasta at the source code of an app you already run and it rebuilds it here, screen by screen."
            cta="Adopt from source"
            onClick={() => navigate('/create-from-git?tab=source')}
          />
          <StartCard
            compact
            icon={GitBranch}
            title="Import from Git"
            blurb="Clone a Twasta project you or a teammate already pushed to a Git repository."
            cta="Pull from Git"
            onClick={() => navigate('/create-from-git')}
          />
          {shown.length === 0 && (
            <StartCard
              compact
              icon={FolderOpen}
              title="Open a shared application"
              blurb="Someone may have shared an application with you — check the picker for anything waiting."
              cta="Open project"
              accentBadge={sharedCount > 0 ? `${sharedCount} shared` : undefined}
              onClick={() => setProjectModalOpen(true)}
            />
          )}
        </div>
      </div>

      {/* What the Workbench does — the part the old empty state never said. */}
      <div>
        {sectionTitle('What you can build here', 'Everything below unlocks once an application is open.')}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {WORKBENCH_CAPABILITIES.map((c) => (
            <div
              key={c.title}
              style={{
                display: 'flex',
                gap: 12,
                padding: '0.875rem',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                background: 'transparent',
              }}
            >
              <span
                className="material-icons"
                style={{ fontSize: 21, color: 'var(--color-accent)', marginTop: 1 }}
              >
                {c.icon}
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{c.title}</span>
                <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                  {c.blurb}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Name the analytics application, then straight into Guided Build. */}
      <Modal
        open={analyticsOpen}
        onClose={() => { if (!creatingAnalytics) setAnalyticsOpen(false) }}
        title="Build an analytics application"
      >
        <p style={{ margin: '0 0 1rem', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
          Name it, and the guided build opens next: connect your warehouse
          (Snowflake, BigQuery, Dremio, SAP HANA or any database), explore its
          tables, and generate the data model, visuals and dashboards from them.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
              Name
            </label>
            <input
              autoFocus
              type="text"
              value={analyticsTitle}
              onChange={(e) => {
                setAnalyticsTitle(e.target.value)
                // The ID follows the name until the user edits it themselves.
                if (!analyticsIdTouched) {
                  setAnalyticsId(
                    e.target.value.trim().replace(/\s+/g, '_').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64),
                  )
                }
              }}
              placeholder="Sales Analytics"
              disabled={creatingAnalytics}
              style={{
                padding: '0.5rem 0.625rem',
                fontSize: '0.9375rem',
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text)',
                fontFamily: 'var(--font-primary)',
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
              Project ID <span style={{ fontWeight: 400 }}>(filled in for you)</span>
            </label>
            <input
              type="text"
              value={analyticsId}
              onChange={(e) => { setAnalyticsId(e.target.value); setAnalyticsIdTouched(true) }}
              placeholder="sales_analytics"
              disabled={creatingAnalytics}
              pattern="[A-Za-z0-9_-]+"
              style={{
                padding: '0.5rem 0.625rem',
                fontSize: '0.9375rem',
                background: 'var(--color-bg)',
                border: `1px solid ${analyticsId && !/^[A-Za-z0-9_-]+$/.test(analyticsId) ? 'var(--color-error, #dc2626)' : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text)',
                fontFamily: 'var(--font-mono, monospace)',
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button
            className="btn btn-secondary"
            onClick={() => setAnalyticsOpen(false)}
            disabled={creatingAnalytics}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={createAnalyticsApp}
            disabled={creatingAnalytics || !analyticsTitle.trim() || !/^[A-Za-z0-9_-]+$/.test(analyticsId)}
          >
            {creatingAnalytics ? 'Creating…' : 'Continue'}
          </button>
        </div>
      </Modal>
    </div>
  )
}

/* ----------------------------------------------------------------
   Main WorkbenchPage Component
   ---------------------------------------------------------------- */

/** A URL tab segment the Workbench renders: lowercase id, letters / digits / - _. */
const TAB_ID = /^[a-z][a-z0-9_-]*$/

export default function WorkbenchPage() {
  const { currentProject, activeTab, fetchProjects, selectProject, setActiveTab, loading, error } = useProjectStore()
  // Announce anything changed OUTSIDE the browser since this project was last
  // opened — an SSH-console edit, a laptop agent's push, a `git pull`. Without
  // this such a change appears silently on the next load, and a browser save
  // overwrites it with no conflict detection (Phase 4 of the SSH agent-console
  // plan). Re-uses the dock's own changed-files event, so every designer's
  // existing refresh/evict wiring picks it up.
  useOutOfBandChanges(currentProject?.application.folder_name)
  const setProjectModalOpen = useDockUiStore((s) => s.setProjectModalOpen)
  const [noAppOpen, setNoAppOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ project?: string; tab?: string; sub?: string }>()

  const urlProject = params.project || null
  // An old tab id (overview, prototype, support…) is redirected below; until
  // then it is not applied, so the store never holds a retired id.
  const legacy = params.tab ? redirectForTab(params.tab) : null
  const urlTab: string | null =
    params.tab && TAB_ID.test(params.tab) && !legacy ? params.tab : null
  const urlSub = params.sub && TAB_ID.test(params.sub) ? params.sub : undefined

  // Old links (bookmarks, notifications, designers' back buttons) keep working:
  // /workbench/p/support?issue=3 → /workbench/p/advanced/support?issue=3.
  useEffect(() => {
    if (!urlProject || !legacy) return
    const target = `/workbench/${encodeURIComponent(urlProject)}/${legacy.tab}${legacy.sub ? `/${legacy.sub}` : ''}`
    useProjectStore.getState().setActiveTab(legacy.tab)
    navigate(target + location.search, { replace: true, state: location.state })
  }, [urlProject, legacy?.tab, legacy?.sub]) // eslint-disable-line react-hooks/exhaustive-deps

  // Run-only users (Application User / Runtime User) can't see the design —
  // bounce them to the Run view if they land on the workbench by URL.
  useRunOnlyRedirect(urlProject || undefined)

  // A project switch requested by the URL is async (selectProject fetches),
  // so until the store catches up the store→URL effect must not "correct"
  // the URL back to the previous project. This ref marks the in-flight load.
  const pendingUrlProjectRef = useRef<string | null>(null)

  // The URL tab this effect has already pushed into the store. Comparing
  // against THIS instead of against `activeTab` is what keeps the effect below
  // safe to re-run: `activeTab` is deliberately not a dependency (see the note
  // there), so on a re-run triggered by `currentProject` the closure still
  // holds the value from an earlier render — the comparison stays true, the
  // store is written again, every subscriber re-renders, and the cascade ends
  // in React's "Maximum update depth exceeded" with a blank workbench. A ref is
  // read fresh on every run, so a repeat is simply a no-op.
  const appliedUrlTabRef = useRef<string | null>(null)

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  // ── URL → store ──
  // Back/forward and pasted links land here: make the store follow the URL.
  useEffect(() => {
    if (urlProject && urlProject !== currentProject?.application.folder_name) {
      pendingUrlProjectRef.current = urlProject
      selectProject(urlProject)
    }
    // Act on a CHANGE OF URL, not on a difference from the store. Those two
    // are the same thing the first time and diverge afterwards: the store→URL
    // effect keeps the address bar canonical, so a tab the user picked in the
    // TopBar comes back through here as a changed `urlTab` and is applied
    // normally — while a re-run with an unchanged URL does nothing.
    if (urlTab && urlTab !== appliedUrlTabRef.current) {
      appliedUrlTabRef.current = urlTab
      setActiveTab(urlTab)
    }
    // currentProject is a dep so a URL-requested project re-asserts itself if
    // the localStorage restore (restoreProject) resolves later and overwrites
    // it. activeTab is deliberately NOT a dep — TopBar clicks change the store
    // before the URL and re-running here would revert them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProject, urlTab, currentProject])

  // A URL-requested project that failed to load (deleted / bad link) would
  // otherwise wedge the page on an empty state with a stale URL — fall back
  // to the bare workbench so the user can pick another project.
  useEffect(() => {
    if (pendingUrlProjectRef.current && !loading && error) {
      pendingUrlProjectRef.current = null
      navigate('/workbench', { replace: true })
    }
  }, [loading, error, navigate])

  // ── store → URL ──
  // Keep the address bar canonical: /workbench/<project>/<tab>. Runs when the
  // store changes outside a URL navigation (TopBar tab click, project restore
  // from localStorage, ProjectsModal) so every state is linkable.
  useEffect(() => {
    const folder = currentProject?.application.folder_name
    if (!folder) return
    if (pendingUrlProjectRef.current) {
      // URL-driven switch still loading — don't fight it.
      if (pendingUrlProjectRef.current !== folder) return
      pendingUrlProjectRef.current = null
    }
    // Read the tab from the STORE, not from this render's closure — this is
    // what stops the blank workbench after login.
    //
    // The URL→store effect above runs first in the same pass. When the URL and
    // the store disagree (login always lands on …/overview, while the store
    // still holds the tab localStorage remembered), that effect adopts the
    // URL's tab — but the store write doesn't re-render this pass, so the
    // `activeTab` closed over here is still the OLD one. Acting on it navigates
    // the URL back to the old tab, which the effect above then adopts as a
    // "changed" URL, which brings us back here… the two effects hand the same
    // disagreement back and forth forever (React "Maximum update depth
    // exceeded" → the whole tree unmounts → blank page, with the address bar
    // and history spammed on every lap).
    //
    // Reading fresh means the URL wins on the pass where it was applied and
    // there is nothing left to disagree about. A TopBar click still works: it
    // writes the store first, so the fresh read IS the new tab and the URL
    // follows it.
    if (legacy) return // the redirect above owns this navigation
    let stored = useProjectStore.getState().activeTab
    // A designer's Back lands on bare /workbench with { selectedNodeId }: that
    // node lives in the app tree, so make sure an objects tab is showing.
    const restoreNode = (location.state as { selectedNodeId?: string } | null)?.selectedNodeId
    if (!urlProject && restoreNode) {
      const tabs = useJourneyStore.getState().journey?.tabs || []
      const objectTabs = tabs.filter((t) => t.view === 'objects').map((t) => t.id)
      if (!objectTabs.includes(stored) && !['screens', 'pages', 'dashboards', 'records'].includes(stored)) {
        stored = objectTabs[0] || 'screens'
        useProjectStore.getState().setActiveTab(stored)
      }
    }
    const tab = TAB_ID.test(stored) ? canonicalTab(stored) : 'next'
    // An Advanced tool's sub-path rides along while the tab itself is unchanged.
    const sub = urlTab === tab && urlSub ? `/${urlSub}` : ''
    const want = `/workbench/${encodeURIComponent(folder)}/${tab}${sub}`
    if (location.pathname !== want) {
      // Replace when merely canonicalizing a bare /workbench URL; push when
      // the user actually moved (tab/project change) so Back undoes it.
      // location.state is forwarded because designers navigate back with
      // { selectedNodeId } that PrototypeTab still needs to read.
      // Query params (?obj/?view) belong to the prototype tab — only carry
      // them when the tab itself isn't changing.
      const search = urlTab === tab ? location.search : ''
      navigate(want + search, { replace: !urlProject, state: location.state })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, activeTab])

  // Show the get-started dialog when there's no current project, unless the
  // user previously chose "Don't show this again". Auto-open only ONCE per
  // "no app" episode: once the user closes it (e.g. to pick an app from the
  // rail, which triggers a fetch and toggles `loading`), don't pop it back up.
  // The flag resets when an app actually opens, so deleting the open app
  // correctly re-shows the dialog.
  const autoOpenedRef = useRef(false)
  useEffect(() => {
    if (loading) return
    if (currentProject) {
      autoOpenedRef.current = false
      setNoAppOpen(false)
      return
    }
    if (!isNoAppDialogDismissed() && !autoOpenedRef.current) {
      autoOpenedRef.current = true
      setNoAppOpen(true)
    }
  }, [currentProject, loading])

  if (loading && !currentProject) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 200px)',
      }}>
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  if (!currentProject) {
    return (
      <>
        <EmptyState />
        <NoAppDialog
          open={noAppOpen}
          onClose={() => setNoAppOpen(false)}
          onOpenSidebar={() => setProjectModalOpen(true)}
        />
      </>
    )
  }

  if (currentProject.application.title === 'Invalid project information') {
    return <InvalidProjectView />
  }

  return <SimpleWorkbench tab={canonicalTab(activeTab)} sub={activeTab === 'advanced' ? urlSub : undefined} />
}
